(() => {
  'use strict';

  const root = document.documentElement;
  const main = document.querySelector('#MainContent');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!main || reducedMotion.matches) return;

  root.classList.add('aldren-cinema-ready');

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const activeSections = new Set();
  let heroSection = null;
  let heroMedia = null;
  let frame = 0;

  const cinematicSelectors = [
    '.aldren-signature-showcase',
    '.aldren-luxury',
    '.aldren-reviews',
    '.media-with-content'
  ];

  const sectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) activeSections.add(entry.target);
            else activeSections.delete(entry.target);
          });
          requestUpdate();
        },
        { rootMargin: '18% 0px 18% 0px', threshold: 0 }
      )
    : null;

  const registerSection = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.aldrenCinema === 'true') return;
    section.dataset.aldrenCinema = 'true';
    section.classList.add('aldren-cinema-section');

    if (sectionObserver) sectionObserver.observe(section);
    else activeSections.add(section);
  };

  const registerScope = (scope = main) => {
    cinematicSelectors.forEach((selector) => {
      if (scope.matches?.(selector)) registerSection(scope);
      scope.querySelectorAll?.(selector).forEach(registerSection);
    });
  };

  const registerHero = () => {
    const firstSection = main.querySelector(':scope > .shopify-section') || main.querySelector('.shopify-section');
    if (!(firstSection instanceof HTMLElement)) return;

    const media = firstSection.querySelector('picture img, [class*="image"] img, img');
    if (!(media instanceof HTMLElement)) return;

    heroSection = firstSection;
    heroMedia = media;
    heroSection.classList.add('aldren-cinema-hero');
    heroMedia.classList.add('aldren-cinema-hero__media');
  };

  const updateSection = (section, viewportHeight, mobile) => {
    const rect = section.getBoundingClientRect();
    const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));
    const signed = progress - 0.5;
    const distance = mobile ? 12 : 28;

    section.style.setProperty('--aldren-cinema-progress', progress.toFixed(4));
    section.style.setProperty('--aldren-cinema-y', `${(signed * distance).toFixed(2)}px`);
    section.style.setProperty('--aldren-cinema-y-reverse', `${(signed * distance * -0.56).toFixed(2)}px`);
    section.style.setProperty('--aldren-cinema-x', `${(signed * distance * 0.65).toFixed(2)}px`);
    section.style.setProperty('--aldren-cinema-rotate', `${(signed * 7).toFixed(2)}deg`);
  };

  const updateHero = (viewportHeight, mobile) => {
    if (!heroSection || !heroMedia) return;

    const rect = heroSection.getBoundingClientRect();
    const progress = clamp(-rect.top / Math.max(rect.height, viewportHeight));
    const travel = mobile ? 18 : 42;
    const scale = 1 + progress * (mobile ? 0.018 : 0.032);

    heroSection.style.setProperty('--aldren-hero-scroll-y', `${(progress * travel).toFixed(2)}px`);
    heroSection.style.setProperty('--aldren-hero-scroll-scale', scale.toFixed(4));
    heroSection.style.setProperty('--aldren-hero-copy-y', `${(progress * travel * -0.3).toFixed(2)}px`);
  };

  const update = () => {
    frame = 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const mobile = window.innerWidth < 750;

    updateHero(viewportHeight, mobile);
    activeSections.forEach((section) => updateSection(section, viewportHeight, mobile));
  };

  function requestUpdate() {
    if (frame || document.hidden) return;
    frame = window.requestAnimationFrame(update);
  }

  registerHero();
  registerScope();
  requestUpdate();

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  window.addEventListener('orientationchange', requestUpdate, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) requestUpdate();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    registerScope(event.target);
    registerHero();
    requestUpdate();
  });

  reducedMotion.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    root.classList.remove('aldren-cinema-ready');
    sectionObserver?.disconnect();
    activeSections.clear();
  });
})();
